/*
 * Store finder.
 *
 * The map, its pins and the list beside it are three views of one set of
 * locations, so they are driven from one place: the element holds the stores,
 * and every filter runs through updateFilter() rather than each view deciding
 * for itself what to show.
 *
 * Locations and the tag vocabulary come from the store locator service, not
 * from the theme, so the section carries their addresses as settings and the
 * theme never has to be republished when a stockist is added.
 */
class BhaktaStoreFinder extends HTMLElement {
  connectedCallback() {
    this.mapEl = this.querySelector('[data-bk-map]');
    this.listEl = this.querySelector('[data-bk-list]');
    this.searchEl = this.querySelector('[data-bk-geocoder]');
    this.filterEl = this.querySelector('[data-bk-filters]');
    if (!this.mapEl) return;

    this.stores = { type: 'FeatureCollection', features: [] };
    this.home = [Number(this.dataset.lng || -100), Number(this.dataset.lat || 40)];
    this.homeZoom = Number(this.dataset.zoom || 3);

    this.whenReady().then(() => this.build());
  }

  /*
   * Mapbox arrives from its own CDN on a deferred tag, so it may not be there
   * when this element upgrades. Waiting on the global is simpler than sequencing
   * two script tags, and it fails quietly on a slow or blocked network.
   */
  whenReady(tries = 100) {
    if (window.mapboxgl && window.MapboxGeocoder) return Promise.resolve();
    if (tries <= 0) return Promise.reject(new Error('Mapbox did not load'));
    return new Promise((done) => setTimeout(done, 100)).then(() => this.whenReady(tries - 1));
  }

  build() {
    mapboxgl.accessToken = this.dataset.token;

    this.map = new mapboxgl.Map({
      container: this.mapEl,
      style: this.dataset.style,
      center: this.home,
      zoom: this.homeZoom,
      scrollZoom: true,
      projection: { name: 'mercator' },
      cooperativeGestures: true,
    });

    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    this.map.on('load', () => this.loadStores());

    if (this.searchEl) {
      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl,
        marker: false,
        placeholder: this.dataset.placeholder || 'Search by city, state, or postcode',
      });
      this.searchEl.appendChild(geocoder.onAdd(this.map));
    }

    this.loadTags();
  }

  /* One fetch helper, one retry: the service is occasionally slow to wake. */
  async read(url) {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await fetch(url);
        if (response.ok) return response.json();
      } catch (e) {
        /* Fall through to the retry, then to the empty result. */
      }
    }
    return null;
  }

  async loadStores() {
    const data = await this.read(this.dataset.locations);
    if (!data || !data.features) {
      this.fail(this.dataset.errorText || 'The store list could not be loaded.');
      return;
    }

    this.stores.features = data.features;

    this.map.loadImage(this.dataset.marker, (error, image) => {
      if (!error && image && !this.map.hasImage('bk-marker')) {
        this.map.addImage('bk-marker', image);
      }

      this.map.addLayer({
        id: 'locations',
        type: 'symbol',
        source: { type: 'geojson', data: this.stores },
        layout: {
          'icon-image': error ? '' : 'bk-marker',
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-offset': [0, -25],
        },
      });

      this.buildList();
      this.bindMap();
      this.updateFilter(false);
    });
  }

  async loadTags() {
    const data = await this.read(this.dataset.tags);
    if (!data || !data.filter_tags || !this.filterEl) return;

    /* The outlet chain reads as a place to shop, the rest as what is on the
     * shelf, so they are offered as two questions instead of one long row. */
    const outlet = this.dataset.outletTag;
    const outlets = data.filter_tags.filter((tag) => tag === outlet);
    const products = data.filter_tags.filter((tag) => tag !== outlet);

    if (outlets.length) this.addFilterGroup(this.dataset.storeLabel || 'Filter by store', outlets);
    if (products.length) this.addFilterGroup(this.dataset.productLabel || 'Filter by product', products);

    this.readFiltersFromUrl();
  }

  addFilterGroup(title, tags) {
    const group = document.createElement('fieldset');
    group.className = 'bk-finder__group';

    const legend = document.createElement('legend');
    legend.className = 'bk-finder__legend';
    legend.textContent = title;
    group.appendChild(legend);

    const row = document.createElement('div');
    row.className = 'bk-finder__chips';

    tags.forEach((tag) => {
      const label = document.createElement('label');
      label.className = 'bk-finder__chip';

      const box = document.createElement('input');
      box.type = 'checkbox';
      box.name = 'tags';
      box.value = tag;
      box.addEventListener('change', () => this.updateFilter(true));

      label.appendChild(box);
      label.appendChild(document.createTextNode(tag));
      row.appendChild(label);
    });

    group.appendChild(row);
    this.filterEl.appendChild(group);
  }

  get checkedTags() {
    return Array.from(this.querySelectorAll('input[name="tags"]:checked')).map((b) => b.value);
  }

  /* The chosen filters live in the URL so a shortlist can be sent to someone. */
  writeFiltersToUrl(tags) {
    const url = new URL(window.location.href);
    if (tags.length) url.searchParams.set('tags', tags.join(','));
    else url.searchParams.delete('tags');
    window.history.replaceState({}, '', url);
  }

  readFiltersFromUrl() {
    const param = new URL(window.location.href).searchParams.get('tags');
    if (!param) return;
    const wanted = param.split(',').map((tag) => tag.trim());
    this.querySelectorAll('input[name="tags"]').forEach((box) => {
      box.checked = wanted.includes(box.value);
    });
    this.updateFilter(false);
  }

  updateFilter(syncUrl) {
    const tags = this.checkedTags;
    if (syncUrl) this.writeFiltersToUrl(tags);

    const popup = this.querySelector('.mapboxgl-popup');
    if (popup) popup.remove();

    if (!this.map.getLayer('locations')) return;

    if (!tags.length) {
      this.map.setFilter('locations', null);
      this.filterList([]);
      return;
    }

    this.map.setFilter('locations', ['any', ...tags.map((tag) => ['in', tag, ['get', 'tags']])]);
    this.filterList(tags);
    setTimeout(() => this.frameVisible(tags), 100);
  }

  filterList(tags) {
    let shown = 0;
    this.querySelectorAll('.bk-finder__item').forEach((card) => {
      const own = (card.dataset.tags || '').split(',').map((t) => t.trim());
      const visible = !tags.length || tags.some((tag) => own.includes(tag));
      card.hidden = !visible;
      if (visible) shown += 1;
    });

    const empty = this.querySelector('[data-bk-empty]');
    if (empty) empty.hidden = shown > 0;
  }

  /*
   * Returning to the whole country first, then framing what is left, keeps the
   * movement readable: a filter that strands the reader mid-continent looks
   * like a failure even when the right pins are on screen.
   */
  frameVisible(tags) {
    this.map.flyTo({ center: this.home, zoom: this.homeZoom, speed: 2, curve: 1 });

    this.map.once('moveend', () => {
      const features = this.map.querySourceFeatures('locations');
      if (!features.length) return;

      const bounds = new mapboxgl.LngLatBounds();
      features.forEach((feature) => {
        if (feature.geometry && feature.geometry.type !== 'Point') return;
        const own = this.tagsOf(feature);
        if (!tags.length || tags.some((tag) => own.includes(tag))) {
          bounds.extend(feature.geometry.coordinates);
        }
      });

      if (!bounds.isEmpty()) this.map.fitBounds(bounds, { padding: 60, duration: 1000, maxZoom: 14 });
    });
  }

  /* The service sends tags as an array, the vector tiles as a JSON string. */
  tagsOf(feature) {
    const raw = feature.properties ? feature.properties.tags : null;
    if (Array.isArray(raw)) return raw;
    try {
      return JSON.parse(raw) || [];
    } catch (e) {
      return [];
    }
  }

  buildList() {
    const fragment = document.createDocumentFragment();

    this.stores.features.forEach((store, index) => {
      const p = store.properties || {};
      const id = store.id != null ? store.id : index;

      const item = document.createElement('article');
      item.className = 'bk-finder__item';
      item.id = `bk-finder-${id}`;
      item.dataset.id = String(id);
      if (Array.isArray(p.tags)) item.dataset.tags = p.tags.join(',');

      const where = [p.address, p.city, p.state, p.postalCode].filter(Boolean).join(', ');
      const chips = (Array.isArray(p.tags) ? p.tags : [])
        .map((tag) => `<span class="bk-finder__tag">${this.clean(tag)}</span>`)
        .join('');

      item.innerHTML = `
        <h3 class="bk-finder__name">${this.clean(p.name || '')}</h3>
        ${chips ? `<div class="bk-finder__tags">${chips}</div>` : ''}
        <p class="bk-finder__address">${this.clean(p.address || '')}</p>
        <p class="bk-finder__city">${this.clean([p.city, p.state, p.postalCode].filter(Boolean).join(', '))}</p>
        <div class="bk-finder__actions">
          <a class="bk-link" href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(where)}" target="_blank" rel="noopener">Directions</a>
          ${p.website ? `<a class="bk-link" href="${this.clean(p.website)}" target="_blank" rel="noopener">Website</a>` : ''}
        </div>
      `;

      /*
       * Directions and Website are links inside a card that is itself a target,
       * so a click on one of them must not also move the map.
       */
      item.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        event.preventDefault();
        this.flyToStore(store);
        this.markActive(item);
      });

      fragment.appendChild(item);
    });

    const empty = document.createElement('p');
    empty.className = 'bk-finder__empty';
    empty.setAttribute('data-bk-empty', '');
    empty.hidden = true;
    empty.textContent = this.dataset.emptyText || 'No stockists match those filters.';
    fragment.appendChild(empty);

    this.listEl.innerHTML = '';
    this.listEl.appendChild(fragment);
  }

  bindMap() {
    this.map.on('click', (event) => {
      const hits = this.map.queryRenderedFeatures(event.point, { layers: ['locations'] });
      if (!hits.length) return;

      const hit = hits[0];
      this.flyToStore(hit);
      this.markActive(this.querySelector(`#bk-finder-${hit.id}`));
    });
  }

  markActive(item) {
    this.querySelectorAll('.bk-finder__item.is-active').forEach((el) => el.classList.remove('is-active'));
    if (item) item.classList.add('is-active');
  }

  /*
   * The pin the popup hangs from has to be the one the map has drawn, not the
   * one the service sent: only the drawn feature carries the coordinates the
   * current projection is using. So the map is flown first, and the feature is
   * picked back up from the tiles around the new centre once it settles.
   *
   * The card is deliberately not scrolled into view. Asking for that while the
   * page itself is scrollable walks up the ancestors and takes the whole window
   * with it, which is what threw the reader back to the top of the page.
   */
  flyToStore(feature) {
    this.map.flyTo({ center: feature.geometry.coordinates, zoom: 15, speed: 2 });

    const open = this.querySelector('.mapboxgl-popup');
    if (open) open.remove();

    this.map.once('moveend', () => {
      const centre = this.map.project(this.map.getCenter());
      const box = 500;
      const found = this.map.queryRenderedFeatures(
        [
          [centre.x - box, centre.y - box],
          [centre.x + box, centre.y + box],
        ],
        { layers: ['locations'] }
      );

      const drawn = found.find((f) => f.id === feature.id) || feature;
      this.openPopup(drawn);
    });
  }

  openPopup(feature) {
    const p = feature.properties || {};
    const where = [p.address, p.city, p.state, p.postalCode].filter(Boolean).join(', ');
    const chips = this.tagsOf(feature)
      .map((tag) => `<span class="bk-finder__tag">${this.clean(tag)}</span>`)
      .join('');

    new mapboxgl.Popup({ closeOnClick: false, offset: [0, -30] })
      .setLngLat(feature.geometry.coordinates)
      .setHTML(
        `<p class="bk-finder__popup-name">${this.clean(p.name || '')}</p>` +
          (chips ? `<div class="bk-finder__tags">${chips}</div>` : '') +
          `<p class="bk-finder__popup-where">${this.clean(p.address || '')}</p>` +
          `<p class="bk-finder__popup-where">${this.clean([p.city, p.state, p.postalCode].filter(Boolean).join(', '))}</p>` +
          `<div class="bk-finder__actions">` +
          `<a class="bk-link" href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(where)}" target="_blank" rel="noopener">Directions</a>` +
          (p.website ? `<a class="bk-link" href="${this.clean(p.website)}" target="_blank" rel="noopener">Website</a>` : '') +
          `</div>`
      )
      .addTo(this.map);
  }

  fail(message) {
    if (this.listEl) this.listEl.innerHTML = `<p class="bk-finder__empty">${this.clean(message)}</p>`;
  }

  clean(value) {
    const box = document.createElement('div');
    box.textContent = value;
    return box.innerHTML;
  }
}

if (!customElements.get('bhakta-store-finder')) {
  customElements.define('bhakta-store-finder', BhaktaStoreFinder);
}
